<script setup lang="ts">
import { InfoTip } from '@it-tools/shell'
import { useI18n } from 'vue-i18n'
import { useFormat } from '../composables/useFormat'
import { instanceKey, type ProviderId } from '../model/catalog'
import type { NodeFit } from '../model/fit'
import type { Interval } from '../model/ranking'

/**
 * The cheapest node for this pod on every cloud, so the comparison that matters
 * most is one click away. Computed on the device from the bundled catalogue.
 */
defineProps<{
  entries: readonly {
    readonly provider: ProviderId
    /** False while the provider's catalogue is still downloading. */
    readonly loaded: boolean
    readonly fit: NodeFit | undefined
  }[]
  hours: number
  interval: Interval
}>()

const emit = defineEmits<{ compare: [string] }>()

const { t } = useI18n()
const format = useFormat()
</script>

<template>
  <section class="elsewhere" aria-labelledby="elsewhere-heading">
    <h2 id="elsewhere-heading" class="elsewhere__heading">
      {{ t('elsewhere.heading') }}
      <InfoTip :label="t('elsewhere.heading')" :text="t('elsewhere.help')" />
    </h2>
    <ul class="elsewhere__list">
      <li v-for="entry in entries" :key="entry.provider" class="elsewhere__item">
        <span class="elsewhere__provider">{{ t(`provider.service.${entry.provider}`) }}</span>
        <template v-if="entry.fit">
          <span class="elsewhere__instance mono">{{ entry.fit.instance.name }}</span>
          <span class="elsewhere__price">
            <span class="mono">{{ format.price(entry.fit.costPerPod * hours) }}</span>
            {{ t(`summary.perPodPer.${interval}`) }}
          </span>
          <button
            type="button"
            :aria-label="t('elsewhere.compareLabel', { name: entry.fit.instance.name })"
            @click="emit('compare', instanceKey(entry.fit.instance))"
          >
            {{ t('elsewhere.compare') }}
          </button>
        </template>
        <span v-else-if="entry.loaded" class="elsewhere__none">{{ t('elsewhere.none') }}</span>
        <span v-else class="elsewhere__none">{{ t('common.loading') }}</span>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.elsewhere {
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
}

.elsewhere__heading {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0 0 var(--space-3);
  font-size: var(--text-base);
}

.elsewhere__list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.elsewhere__item {
  display: grid;
  grid-template-columns: 8rem minmax(0, 1fr) auto auto;
  align-items: center;
  gap: var(--space-2) var(--space-3);
  padding: var(--space-2) 0;
  font-size: var(--text-sm);
}

.elsewhere__item + .elsewhere__item {
  border-top: 1px solid var(--color-border);
}

.elsewhere__provider {
  font-weight: 600;
}

.elsewhere__instance {
  overflow-wrap: anywhere;
}

.elsewhere__price {
  color: var(--color-text-muted);
  white-space: nowrap;
}

.elsewhere__price .mono {
  color: var(--color-text);
}

.elsewhere__none {
  grid-column: 2 / -1;
  color: var(--color-text-muted);
}

button {
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

button:hover {
  background: var(--color-surface-raised);
  border-color: var(--color-border-strong);
}

@media (max-width: 36rem) {
  .elsewhere__item {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .elsewhere__price {
    grid-column: 1;
  }
}
</style>
